import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil, finalize, take, debounceTime } from 'rxjs';
import { FilterService, FilterOption } from '../../../core';
import { AuthenticationService } from '../../../core';
import { ReportsService } from '../../../core';
import { FilterChangeEvent } from '../../models/filter-change-event.model';
import { buildReportOtherFilters } from '../../utils/filter-utils';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-report-filters',
  templateUrl: './report-filters.component.html',
  styleUrls: ['./report-filters.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class ReportFiltersComponent implements OnInit, OnDestroy, OnChanges {

  @Input() reportKey: string = 'crm/crm';
  @Input() showDateFilters: boolean = true;
  @Input() showAdditionalFilters: boolean = true;
  @Input() additionalFilterTypes: string[] = ['companies', 'users', 'clients'];
  @Input() isPdf: boolean = false;
  @Input() filterMode: string = 'crm'; // 'crm', 'crm-totals', 'finance', 'projects', 'inventory-operations', 'inventory-stock', 'temperature', 'carbonFootprint', 'operations'
  isAuthenticated: boolean = false; // Trust parent authentication state

  @Output() filterChange = new EventEmitter<FilterChangeEvent>();
  @Output() filterReady = new EventEmitter<void>();

  // Date filter properties
  dateType: string = 'ultimo';
  dateNumber: string = '3';
  datePeriod: string = 'mes';
  startDate: Date = new Date('2025-06-25');
  endDate: Date = new Date('2025-09-23');

  // Additional filter properties - now support multiple selection
  selectedCompanies: string[] | null = null;
  selectedUsers: string[] | null = null;
  selectedClients: string[] | null = null;

  // Projects mode filter properties
  selectedProjects: string[] | null = null;
  selectedMerchandise: string[] | null = null;
  selectedStatus: string[] | null = null;
  selectedMBL: string[] | null = null;
  selectedHBL: string[] | null = null;

  // Inventory mode filter properties
  selectedOrigin: string[] | null = null;
  selectedDestination: string[] | null = null;
  selectedSupplier: string[] | null = null;
  selectedProduct: string[] | null = null;
  selectedBatch: string[] | null = null;
  selectedSerialNumber: string[] | null = null;
  selectedLocation: string[] | null = null;

  // Temperature mode filter properties (reusing selectedProject, selectedMBL, selectedBatch, selectedSerialNumber from other modes)
  selectedBESS: string[] | null = null;
  selectedTracker: string[] | null = null;
  selectedLote: string[] | null = null;
  selectedDestinationTransportRef: string[] | null = null;
  temperatureMin: number = 28;
  temperatureMax: number = 48;

  // Carbon Footprint mode filter properties
  selectedShippingRef: string[] | null = null;
  selectedMBLCarbon: string[] | null = null;
  selectedContainer: string[] | null = null;
  selectedProjectCarbon: string | null = null;

  // Operations mode filter properties
  selectedProject: string[] | null = null;
  selectedOperationsContainer: string[] | null = null;
  selectedServices: string[] | null = null;
  selectedOperationsMBL: string[] | null = null;
  selectedOperationsHBL: string[] | null = null;
  selectedDateFilterType: string | null = null;

  // CRM Totals mode filter properties
  selectedFilterProperty: string | null = null;
  amountMin: number | null = null;
  amountMax: number | null = null;
  amountSliderMin: number = 0;
  amountSliderMax: number = 1000000;
  amountSliderStep: number = 1000;

  // Store pending amount values to avoid race conditions with ngModel
  private pendingAmountMin: number | null = null;
  private pendingAmountMax: number | null = null;

  // Finance mode filter properties
  selectedCountry: string[] | null = null;
  selectedMargin: string | null = null;

  // CRM mode Boolean filter properties
  withoutBudget: boolean = false;
  withoutBudgetOptions = [
    { label: 'Sin Presupuesto', value: 'without' },
    { label: 'Con Presupuesto', value: 'with' }
  ];

  // Projects mode Boolean filter properties
  inExecution: boolean = false;

  // Filter options
  dateTypeOptions: FilterOption[] = [];
  periodOptions: FilterOption[] = [];
  numberOptions: FilterOption[] = [];
  companyOptions: FilterOption[] = [];
  userOptions: FilterOption[] = [];
  clientOptions: FilterOption[] = [];

  // Projects mode filter options
  projectOptions: FilterOption[] = [];
  merchandiseOptions: FilterOption[] = [];
  statusOptions: FilterOption[] = [];
  mblOptions: FilterOption[] = [];
  hblOptions: FilterOption[] = [];

  // Inventory mode filter options
  originOptions: FilterOption[] = [];
  destinationOptions: FilterOption[] = [];
  supplierOptions: FilterOption[] = [];
  productOptions: FilterOption[] = [];
  batchOptions: FilterOption[] = [];
  serialNumberOptions: FilterOption[] = [];
  locationOptions: FilterOption[] = [];

  // Temperature mode filter options
  bessOptions: FilterOption[] = [];
  trackerOptions: FilterOption[] = [];
  loteOptions: FilterOption[] = [];
  destinationTransportRefOptions: FilterOption[] = [];

  // Carbon Footprint mode filter options
  shippingRefOptions: FilterOption[] = [];
  mblCarbonOptions: FilterOption[] = [];
  containerOptions: FilterOption[] = [];
  projectCarbonOptions: FilterOption[] = [];

  // Operations mode filter options
  operationsProjectOptions: FilterOption[] = [];
  operationsContainerOptions: FilterOption[] = [];
  servicesOptions: FilterOption[] = [];
  operationsMBLOptions: FilterOption[] = [];
  operationsHBLOptions: FilterOption[] = [];
  dateFilterTypeOptions: FilterOption[] = []; // Se llenará desde el backend

  // Finance mode filter options
  countryOptions: FilterOption[] = [];
  marginOptions: FilterOption[] = [];

  // CRM Totals mode filter options
  filterPropertyOptions: FilterOption[] = [];

  // Loading states
  loadingCompanies: boolean = false;
  loadingUsers: boolean = false;
  loadingClients: boolean = false;

  // Projects mode loading states
  loadingProjects: boolean = false;
  loadingMerchandise: boolean = false;
  loadingStatus: boolean = false;
  loadingMBL: boolean = false;
  loadingHBL: boolean = false;

  // Inventory mode loading states
  loadingOrigin: boolean = false;
  loadingDestination: boolean = false;
  loadingSupplier: boolean = false;
  loadingProduct: boolean = false;
  loadingBatch: boolean = false;
  loadingSerialNumber: boolean = false;
  loadingLocation: boolean = false;

  // Temperature mode loading states
  loadingBESS: boolean = false;
  loadingTracker: boolean = false;
  loadingLote: boolean = false;
  loadingDestinationTransportRef: boolean = false;

  // Carbon Footprint mode loading states
  loadingShippingRef: boolean = false;
  loadingMBLCarbon: boolean = false;
  loadingContainer: boolean = false;
  loadingProjectCarbon: boolean = false;

  // Operations mode loading states
  loadingProject: boolean = false;
  loadingOperationsContainer: boolean = false;
  loadingServices: boolean = false;
  loadingOperationsMBL: boolean = false;
  loadingOperationsHBL: boolean = false;
  loadingDateFilterType: boolean = false;

  // Finance mode loading states
  loadingCountry: boolean = false;
  loadingMargin: boolean = false;

  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();
  private pendingRequests: number = 0;

  // Debounce subject for amount inputs - stores the latest values to avoid race conditions
  private amountChangeSubject$ = new Subject<{ amountMin: number | null, amountMax: number | null }>();

  // Store latest values immediately when input changes to ensure we capture the right value
  private latestAmountMin: number | null = null;
  private latestAmountMax: number | null = null;

  // Flag to prevent multiple simultaneous filter option loads
  private isLoadingFilterOptions: boolean = false;

  constructor(
    private filterService: FilterService,
    private reportsService: ReportsService,
    private authenticationService: AuthenticationService
  ) {
    // Suscribirse a ambos observables de credenciales
    // Cuando cualquiera cambie, actualizar el estado según el filterMode actual
    // Nota: No cargar opciones aquí para evitar peticiones duplicadas
    // Las opciones se cargarán en ngOnInit cuando el componente esté listo
    this.authenticationService.isOdooCredentialsValid$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateAuthenticationState();
      });

    this.authenticationService.isMojitoCredentialsValid$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateAuthenticationState();
      });
  }

  ngOnInit(): void {
    this.initializeFilters();

    // Verificar el estado de autenticación inicial según el filterMode actual
    this.updateAuthenticationState();

    // Initialize active filters based on filter mode
    this.filterService.initializeActiveFilters(this.filterMode);

    // Trust parent authentication state instead of checking Odoo credentials
    // Only load filter options if authenticated and component is visible
    // The component visibility is controlled by *ngIf in the parent template
    if (this.isAuthenticated) {
      // Use setTimeout to ensure component is fully initialized and visible
      setTimeout(() => {
        if (this.isAuthenticated && !this.isLoadingFilterOptions) {
          this.loadFilterOptions();
        }
      }, 0);
    }

    // Setup debounce for amount inputs to avoid triggering requests on every keystroke
    this.amountChangeSubject$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe((values) => {
      // Use the values passed from onAmountChange to avoid race conditions
      // These values are captured at the time the input changed, not when debounce executes
      // This ensures we use the actual input values, not stale model values
      const currentAmountMin = values.amountMin;
      const currentAmountMax = values.amountMax;

      // Normalize values: convert to number or null
      // Handle null, undefined, empty string, or NaN as null (0 is a valid value)
      const amountMinStr = String(currentAmountMin || '');
      const amountMinValue = (currentAmountMin === null ||
        currentAmountMin === undefined ||
        amountMinStr.trim() === '' ||
        isNaN(Number(currentAmountMin)))
        ? null
        : Number(currentAmountMin);
      const amountMaxStr = String(currentAmountMax || '');
      const amountMaxValue = (currentAmountMax === null ||
        currentAmountMax === undefined ||
        amountMaxStr.trim() === '' ||
        isNaN(Number(currentAmountMax)))
        ? null
        : Number(currentAmountMax);

      // Update local values FIRST with normalized values
      this.amountMin = amountMinValue;
      this.amountMax = amountMaxValue;

      // Update service with normalized values atomically to avoid race conditions
      this.filterService.updateFilters({
        amountMin: amountMinValue,
        amountMax: amountMaxValue
      });

      // Emit filter change immediately - service update is synchronous via BehaviorSubject
      // The values are already in the service, so emitFilterChange will use the updated values
      this.emitFilterChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if filterMode has changed
    if (changes['filterMode'] && !changes['filterMode'].firstChange) {
      // Actualizar el estado de autenticación según el nuevo filterMode
      this.updateAuthenticationState();

      // Reset filters for new mode to clear old options and values
      this.resetFiltersForNewMode();

      // Reinitialize active filters with new filter mode
      this.filterService.initializeActiveFilters(this.filterMode);

      // Load filter options for the new mode only if authenticated
      if (this.isAuthenticated && !this.isLoadingFilterOptions) {
        this.loadFilterOptions();
      }
    }

    // Check if reportKey has changed
    if (changes['reportKey'] && !changes['reportKey'].firstChange) {
      if (this.isAuthenticated && !this.isLoadingFilterOptions) {
        this.loadFilterOptions();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Actualiza el estado de autenticación según el filterMode actual
   */
  private updateAuthenticationState(): void {
    // Determinar qué observable usar según el filterMode
    if (this.filterMode === 'temperature' || this.filterMode === 'projects' || this.filterMode === 'carbonFootprint' || this.filterMode === 'operations' || this.filterMode === 'finance') {
      // Usar credenciales de Mojito
      this.isAuthenticated = this.authenticationService.isMojitoCredentialsValid$.value;
    } else {
      // Usar credenciales de Odoo
      this.isAuthenticated = !!this.authenticationService.isOdooCredentialsValid$.value;
    }
  }

  /**
   * Reset filters when changing to a new mode
   */
  private resetFiltersForNewMode(): void {
    // Reset all filter selections
    this.selectedCompanies = null;
    this.selectedUsers = null;
    this.selectedClients = null;
    this.selectedProjects = null;
    this.selectedMerchandise = null;
    this.selectedStatus = null;
    this.selectedMBL = null;
    this.selectedHBL = null;
    this.selectedOrigin = null;
    this.selectedDestination = null;
    this.selectedSupplier = null;
    this.selectedProduct = null;
    this.selectedBatch = null;
    this.selectedSerialNumber = null;
    this.selectedLocation = null;
    this.selectedBESS = null;
    this.selectedTracker = null;
    this.selectedLote = null;
    this.selectedDestinationTransportRef = null;
    this.temperatureMin = 28;
    this.temperatureMax = 48;
    this.selectedShippingRef = null;
    this.selectedMBLCarbon = null;
    this.selectedContainer = null;
    this.selectedProjectCarbon = null;
    this.selectedProject = null;
    this.selectedOperationsContainer = null;
    this.selectedServices = null;
    this.selectedOperationsMBL = null;
    this.selectedDateFilterType = null;  // 👈 AGREGAR ESTA LÍNEA
    this.selectedOperationsHBL = null;
    // Finance
    this.selectedCountry = null;
    this.selectedMargin = null;
    // CRM Totals
    this.selectedFilterProperty = null;
    this.amountMin = null;
    this.amountMax = null;
    // Projects Boolean
    this.inExecution = false;

    // Reset filter options arrays
    this.companyOptions = [];
    this.userOptions = [];
    this.clientOptions = [];
    this.projectOptions = [];
    this.merchandiseOptions = [];
    this.statusOptions = [];
    this.mblOptions = [];
    this.hblOptions = [];
    this.originOptions = [];
    this.destinationOptions = [];
    this.supplierOptions = [];
    this.productOptions = [];
    this.batchOptions = [];
    this.serialNumberOptions = [];
    this.locationOptions = [];
    this.bessOptions = [];
    this.trackerOptions = [];
    this.loteOptions = [];
    this.destinationTransportRefOptions = [];
    this.shippingRefOptions = [];
    this.mblCarbonOptions = [];
    this.containerOptions = [];
    this.projectCarbonOptions = [];
    this.operationsProjectOptions = [];
    this.operationsContainerOptions = [];
    this.servicesOptions = [];
    this.operationsMBLOptions = [];
    this.operationsHBLOptions = [];
    this.dateFilterTypeOptions = [];
    this.countryOptions = [];
    this.marginOptions = [];
    this.filterPropertyOptions = [];
  }

  /**
   * Initialize filters and load initial data
   */
  private initializeFilters(): void {
    // Initialize filter options
    this.dateTypeOptions = this.filterService.getDateTypeOptions();
    this.periodOptions = this.filterService.getPeriodOptions();
    this.numberOptions = this.filterService.getNumberOptions();

    // Sync date selects from service config
    const cfg = this.filterService.dateFilterConfig;
    if (cfg) {
      this.dateType = cfg.type;
      this.dateNumber = String(cfg.number);
      this.datePeriod = cfg.period;
    }

    // Subscribe to date changes
    this.filterService.dateChange$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (dates) => {
        this.startDate = dates.startDate || new Date();
        this.endDate = dates.endDate || new Date();
      }
    });

    // Subscribe to filter changes
    this.filterService.filterChange$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (filters) => {
        // Update local filter values
        if (filters.companies !== undefined) this.selectedCompanies = filters.companies;
        if (filters.users !== undefined) this.selectedUsers = filters.users;
        if (filters.clients !== undefined) this.selectedClients = filters.clients;
        // Projects
        if (filters.projects !== undefined) this.selectedProjects = filters.projects;
        if (filters.merchandise !== undefined) this.selectedMerchandise = filters.merchandise;
        if (filters.status !== undefined) this.selectedStatus = filters.status;
        if (filters.mbl !== undefined) this.selectedMBL = filters.mbl;
        if (filters.hbl !== undefined) this.selectedHBL = filters.hbl;
        // Inventory - operations
        if (filters.origin !== undefined) this.selectedOrigin = filters.origin;
        if (filters.destination !== undefined) this.selectedDestination = filters.destination;
        if (filters.supplier !== undefined) this.selectedSupplier = filters.supplier;
        if (filters.product !== undefined) this.selectedProduct = filters.product;
        if (filters.batch !== undefined) this.selectedBatch = filters.batch;
        if (filters.serialNumber !== undefined) this.selectedSerialNumber = filters.serialNumber;
        // Inventory - stock
        if (filters.location !== undefined) this.selectedLocation = filters.location;
        // Temperature
        if (filters.bess !== undefined) this.selectedBESS = filters.bess;
        if (filters.tracker !== undefined) this.selectedTracker = filters.tracker;
        if (filters.lote !== undefined) this.selectedLote = filters.lote;
        if (filters.destinationTransportRef !== undefined) this.selectedDestinationTransportRef = filters.destinationTransportRef;
        if (filters.temperatureMin !== undefined) this.temperatureMin = filters.temperatureMin;
        if (filters.temperatureMax !== undefined) this.temperatureMax = filters.temperatureMax;
        // Carbon Footprint
        if (filters.shippingRef !== undefined) this.selectedShippingRef = filters.shippingRef;
        if (filters.mblCarbon !== undefined) this.selectedMBLCarbon = filters.mblCarbon;
        if (filters.container !== undefined) this.selectedContainer = filters.container;
        if (filters.projectCarbon !== undefined) this.selectedProjectCarbon = filters.projectCarbon;
        // Operations
        if (filters.operationsProject !== undefined) this.selectedProject = filters.operationsProject;
        if (filters.operationsContainer !== undefined) this.selectedOperationsContainer = filters.operationsContainer;
        if (filters.operationsServices !== undefined) this.selectedServices = filters.operationsServices;
        if (filters.operationsMBL !== undefined) this.selectedOperationsMBL = filters.operationsMBL;
        if (filters.operationsHBL !== undefined) this.selectedOperationsHBL = filters.operationsHBL;
        if (filters.dateFilterType !== undefined) this.selectedDateFilterType = filters.dateFilterType;  // 👈 AGREGAR ESTA LÍNEA
        // CRM Totals - only update if in crm-totals mode
        if (this.filterMode === 'crm-totals') {
          if (filters.filterProperty !== undefined) this.selectedFilterProperty = filters.filterProperty;
          if (filters.amountMin !== undefined) this.amountMin = filters.amountMin;
          if (filters.amountMax !== undefined) this.amountMax = filters.amountMax;
        }
        // Finance - only update if in finance mode
        if (this.filterMode === 'finance') {
          if (filters.country !== undefined) this.selectedCountry = filters.country;
          if (filters.margin !== undefined) this.selectedMargin = filters.margin;
        }
        // CRM - Boolean filters
        if (this.filterMode === 'crm') {
          if (filters.withoutBudget !== undefined) this.withoutBudget = filters.withoutBudget;
        }
        // Projects - Boolean filters
        if (this.filterMode === 'projects') {
          if (filters.inExecution !== undefined) this.inExecution = filters.inExecution;
        }
      }
    });

    // Initialize selected values from current filters so UI reflects persisted state
    const current = this.filterService.getCurrentFilters();
    // Dates from current filters if available
    if (current.dateFrom) {
      this.startDate = new Date(current.dateFrom);
    }
    if (current.dateTo) {
      this.endDate = new Date(current.dateTo);
    }
    this.selectedCompanies = current.companies ?? this.selectedCompanies;
    this.selectedUsers = current.users ?? this.selectedUsers;
    this.selectedClients = current.clients ?? this.selectedClients;
    this.selectedProjects = current.projects ?? this.selectedProjects;
    this.selectedMerchandise = current.merchandise ?? this.selectedMerchandise;
    this.selectedStatus = current.status ?? this.selectedStatus;
    this.selectedMBL = current.mbl ?? this.selectedMBL;
    this.selectedHBL = current.hbl ?? this.selectedHBL;
    this.selectedOrigin = current.origin ?? this.selectedOrigin;
    this.selectedDestination = current.destination ?? this.selectedDestination;
    this.selectedSupplier = current.supplier ?? this.selectedSupplier;
    this.selectedProduct = current.product ?? this.selectedProduct;
    this.selectedBatch = current.batch ?? this.selectedBatch;
    this.selectedSerialNumber = current.serialNumber ?? this.selectedSerialNumber;
    this.selectedLocation = current.location ?? this.selectedLocation;
    this.selectedBESS = current.bess ?? this.selectedBESS;
    this.selectedTracker = current.tracker ?? this.selectedTracker;
    this.selectedLote = current.lote ?? this.selectedLote;
    this.selectedDestinationTransportRef = current.destinationTransportRef ?? this.selectedDestinationTransportRef;
    this.temperatureMin = current.temperatureMin ?? this.temperatureMin;
    this.temperatureMax = current.temperatureMax ?? this.temperatureMax;
    this.selectedShippingRef = current.shippingRef ?? this.selectedShippingRef;
    this.selectedMBLCarbon = current.mblCarbon ?? this.selectedMBLCarbon;
    this.selectedContainer = current.container ?? this.selectedContainer;
    this.selectedProjectCarbon = current.projectCarbon ?? this.selectedProjectCarbon;
    this.selectedProject = current.operationsProject ?? this.selectedProject;
    this.selectedOperationsContainer = current.operationsContainer ?? this.selectedOperationsContainer;
    this.selectedServices = current.operationsServices ?? this.selectedServices;
    this.selectedOperationsMBL = current.operationsMBL ?? this.selectedOperationsMBL;
    this.selectedOperationsHBL = current.operationsHBL ?? this.selectedOperationsHBL;
    this.selectedDateFilterType = current.dateFilterType ?? this.selectedDateFilterType;  // 👈 AGREGAR ESTA LÍNEA
    this.selectedFilterProperty = current.filterProperty ?? this.selectedFilterProperty;
    this.amountMin = current.amountMin ?? this.amountMin;
    this.amountMax = current.amountMax ?? this.amountMax;
    this.selectedCountry = current.country ?? this.selectedCountry;
    this.selectedMargin = current.margin ?? this.selectedMargin;
    this.withoutBudget = current.withoutBudget ?? this.withoutBudget;
    this.inExecution = current.inExecution ?? this.inExecution;

    // Note: loadFilterOptions() is called from ngOnInit() to ensure proper initialization order
    // and to avoid duplicate calls
  }

  /**
   * Load filter options from backend using the filters endpoint
   * Initial load: uses only dates (basic filters) to get all available options
   * Subsequent loads: uses current filters to get filtered options
   */
  private loadFilterOptions(): void {
    // Trust parent authentication state instead of checking Odoo credentials
    if (!this.isAuthenticated) {
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoadingFilterOptions) {
      return;
    }

    const currentFilters = this.filterService.getCurrentFilters();

    // Prepare request body with only dates for initial load
    // This gets all available options without filtering
    const requestBody = {
      dateFrom: currentFilters.dateFrom || this.startDate.toISOString(),
      dateTo: currentFilters.dateTo || this.endDate.toISOString(),
      otherFilters: [] // Empty for initial load to get all options
    };

    // Set loading states
    this.isLoadingFilterOptions = true;
    this.setLoadingStates(true);
    this.startRequest();

    this.reportsService.getReportFilters(this.reportKey, requestBody).pipe(
      take(1),
      finalize(() => {
        this.setLoadingStates(false);
        this.endRequest();
        this.isLoadingFilterOptions = false;
      })
    ).subscribe({
      next: (response) => {
        this.processFilterOptions(response);
      },
      error: (error) => {
        console.error('Error loading filter options:', error);
        this.setFallbackFilterOptions();
      }
    });
  }

  /**
   * Set loading states for all filter types
   */
  private setLoadingStates(loading: boolean): void {
    if (this.filterMode === 'projects') {
      this.loadingProjects = loading;
      this.loadingMerchandise = loading;
      this.loadingStatus = loading;
      this.loadingMBL = loading;
      this.loadingHBL = loading;
    } else if (this.filterMode === 'inventory-operations') {
      this.loadingOrigin = loading;
      this.loadingDestination = loading;
      this.loadingSupplier = loading;
      this.loadingProduct = loading;
      this.loadingBatch = loading;
      this.loadingSerialNumber = loading;
    } else if (this.filterMode === 'inventory-stock') {
      this.loadingProduct = loading;
      this.loadingLocation = loading;
    } else if (this.filterMode === 'temperature') {
      this.loadingBESS = loading;
      this.loadingMBL = loading;
      this.loadingProjects = loading;
      this.loadingTracker = loading;
      this.loadingLote = loading;
      this.loadingDestinationTransportRef = loading;
      this.loadingBatch = loading;
      this.loadingSerialNumber = loading;
    } else if (this.filterMode === 'carbonFootprint') {
      this.loadingShippingRef = loading;
      this.loadingMBLCarbon = loading;
      this.loadingContainer = loading;
      this.loadingProjectCarbon = loading;
    } else if (this.filterMode === 'operations') {
      this.loadingProject = loading;
      this.loadingOperationsContainer = loading;
      this.loadingServices = loading;
      this.loadingOperationsMBL = loading;
      this.loadingOperationsHBL = loading;
      this.loadingDateFilterType = loading;
    } else if (this.filterMode === 'crm-totals') {
      this.loadingCompanies = loading;
    } else if (this.filterMode === 'finance') {
      this.loadingProjects = loading;
      this.loadingCountry = loading;
      this.loadingMargin = loading;
    } else {
      this.loadingCompanies = loading;
      this.loadingUsers = loading;
      this.loadingClients = loading;
    }
  }

  /**
   * Process filter options from backend response
   */
  private processFilterOptions(response: any): void {
    if (Array.isArray(response)) {
      response.forEach((filterGroup: any) => {
        if (this.filterMode === 'projects') {
          switch (filterGroup.id) {
            case 'projects':
              this.projectOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'commodities':
              this.merchandiseOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'status':
              this.statusOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'mbl':
              this.mblOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'hbl':
              this.hblOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'inExecution':
              break;
          }
        } else if (this.filterMode === 'inventory-operations') {
          switch (filterGroup.id) {
            case 'location_id': // Origen
              this.originOptions = this.flattenTreeOptions(filterGroup.options);
              break;
            case 'location_dest_id': // Destino
              this.destinationOptions = this.flattenTreeOptions(filterGroup.options);
              break;
            case 'partner_id': // Proveedor
              this.supplierOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'product_id': // Producto
              this.productOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'x_studio_lotes': // Lote
              this.batchOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'lot_id': // Número de serie
              this.serialNumberOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
          }
        } else if (this.filterMode === 'inventory-stock') {
          switch (filterGroup.id) {
            case 'product_id': // Producto
              this.productOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'location_id': // Ubicación
              this.locationOptions = this.flattenTreeOptions(filterGroup.options);
              break;
          }
        } else if (this.filterMode === 'temperature') {
          switch (filterGroup.id) {
            case 'bess':
              this.bessOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'destinationTransportRef':
              this.destinationTransportRefOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'mbl':
              this.mblOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'projects':
              this.projectOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'tracker':
              this.trackerOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'lote':
              this.loteOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'x_studio_lotes':
              this.batchOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'lot_id': // Número de serie
              this.serialNumberOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
          }
        } else if (this.filterMode === 'carbonFootprint') {
          switch (filterGroup.id) {
            case 'relatedRef':
              this.shippingRefOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'mbl':
              this.mblCarbonOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'containersRef':
              this.containerOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'project':
              this.projectCarbonOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              // Seleccionar automáticamente el primer proyecto de la lista del backend
              if (this.projectCarbonOptions.length > 0 && !this.selectedProjectCarbon) {
                this.selectedProjectCarbon = this.projectCarbonOptions[0].value;
                this.filterService.updateFilter('projectCarbon', this.selectedProjectCarbon);
                this.emitFilterChange();
              }
              break;
          }
        } else if (this.filterMode === 'operations') {
          switch (filterGroup.id) {
            case 'serviceProject':
            case 'project':
            case 'projects':
              this.operationsProjectOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'containerRef':
            case 'container':
            case 'container_number':
              this.operationsContainerOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'service':
            case 'services':
              this.servicesOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'mbl':
              this.operationsMBLOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'hbl':
              this.operationsHBLOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'date':
              this.dateFilterTypeOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));

              // Seleccionar automáticamente el valor por defecto
              const defaultOption = filterGroup.options.find((item: any) => item.default === true);
              if (defaultOption && !this.selectedDateFilterType) {
                this.selectedDateFilterType = defaultOption.id;
                this.filterService.updateFilter('dateFilterType', this.selectedDateFilterType);
              }
              break;
          }
        } else if (this.filterMode === 'crm-totals') {
          switch (filterGroup.id) {
            case 'company_id':
            case 'companies':
              this.companyOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              this.filterService.companyOptions = this.companyOptions;
              break;
            default:
              // Handle NumberRange type filters (stage ranges)
              if (filterGroup.type === 'NumberRange') {
                // Add to filter property options
                const existingIndex = this.filterPropertyOptions.findIndex(opt => opt.value === filterGroup.id);
                if (existingIndex === -1) {
                  // Remove "Rango" from the label if present
                  const label = filterGroup.label ? filterGroup.label.replace(/^Rango\s+/i, '').trim() : filterGroup.label;
                  this.filterPropertyOptions.push({
                    label: label,
                    value: filterGroup.id
                  });
                }

                // Use the first NumberRange filter to set slider bounds
                // All stages should have the same min/max/step values
                if (filterGroup.minValue !== null && filterGroup.minValue !== undefined) {
                  this.amountSliderMin = Number(filterGroup.minValue);
                }
                if (filterGroup.maxValue !== null && filterGroup.maxValue !== undefined) {
                  this.amountSliderMax = Number(filterGroup.maxValue);
                }
                if (filterGroup.step !== null && filterGroup.step !== undefined) {
                  this.amountSliderStep = Number(filterGroup.step);
                }
              }
              break;
          }
        } else if (this.filterMode === 'finance') {
          switch (filterGroup.id) {
            case 'projects':
            case 'project_id':
              this.projectOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'country_id':
            case 'country':
              this.countryOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            default:
              // Handle NumberRange type filters - these become margin options
              if (filterGroup.type === 'NumberRange') {
                // Check if this margin option already exists to avoid duplicates
                const existingIndex = this.marginOptions.findIndex(opt => opt.value === filterGroup.id);
                if (existingIndex === -1) {
                  // Add to margin options only if it doesn't exist
                  this.marginOptions.push({
                    label: filterGroup.label,
                    value: filterGroup.id
                  });
                }

                // Use the first NumberRange filter to set slider bounds
                // All ranges should have the same min/max/step values
                if (this.marginOptions.length === 1) {
                  if (filterGroup.minValue !== null && filterGroup.minValue !== undefined) {
                    this.amountSliderMin = Number(filterGroup.minValue);
                  }
                  if (filterGroup.maxValue !== null && filterGroup.maxValue !== undefined) {
                    this.amountSliderMax = Number(filterGroup.maxValue);
                  }
                  if (filterGroup.step !== null && filterGroup.step !== undefined) {
                    this.amountSliderStep = Number(filterGroup.step);
                  }
                }
              }
              break;
          }
        } else {
          // CRM mode - process all filter types including Boolean
          switch (filterGroup.id) {
            case 'companies':
              this.companyOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              this.filterService.companyOptions = this.companyOptions;
              break;
            case 'users':
              this.userOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'partners': // Note: backend returns 'partners' not 'clients'
              this.clientOptions = filterGroup.options.map((item: any) => ({
                label: item.label,
                value: item.id
              }));
              break;
            case 'withoutBudget':
              // Boolean filter - no options to process, just acknowledge it exists
              // The value is managed by the switch component
              break;
          }
        }
      });
    }
  }

  /**
   * Flatten tree options for location-based filters
   */
  private flattenTreeOptions(options: any[]): any[] {
    const flattened: any[] = [];

    const flatten = (items: any[], prefix: string = '') => {
      items.forEach(item => {
        const label = prefix ? `${prefix} > ${item.label}` : item.label;
        flattened.push({
          label: label,
          value: item.id
        });

        if (item.children && item.children.length > 0) {
          flatten(item.children, label);
        }
      });
    };

    flatten(options);
    return flattened;
  }

  /**
   * Set fallback filter options
   */
  private setFallbackFilterOptions(): void {
    if (this.filterMode === 'projects') {
      this.projectOptions = [
        { label: 'Proyecto A', value: 'project1' },
        { label: 'Proyecto B', value: 'project2' },
        { label: 'Proyecto C', value: 'project3' }
      ];

      this.merchandiseOptions = [
        { label: 'Mercancía 1', value: 'merch1' },
        { label: 'Mercancía 2', value: 'merch2' }
      ];

      this.statusOptions = [
        { label: 'Entregado', value: 'Entregado' },
        { label: 'Arribando', value: 'Arribando' },
        { label: 'En operación', value: 'En operación' }
      ];

      this.mblOptions = [
        { label: 'MBL001', value: 'mbl1' },
        { label: 'MBL002', value: 'mbl2' }
      ];

      this.hblOptions = [
        { label: 'HBL001', value: 'hbl1' },
        { label: 'HBL002', value: 'hbl2' }
      ];
    } else if (this.filterMode === 'inventory-operations') {
      this.originOptions = [
        { label: 'Partners/Vendors', value: 'origin1' },
        { label: 'W0002/S', value: 'origin2' }
      ];

      this.destinationOptions = [
        { label: 'W0002/I', value: 'destination1' },
        { label: 'Partners/Customers', value: 'destination2' }
      ];

      this.supplierOptions = [
        { label: 'Sparber Chile SA', value: 'supplier1' },
        { label: 'Proyectos Gabriela Leal E.I.R.L.', value: 'supplier2' }
      ];

      this.productOptions = [
        { label: 'BESS', value: 'product1' },
        { label: 'Nitrato de', value: 'product2' }
      ];

      this.batchOptions = [
        { label: 'Lote 001', value: 'batch1' },
        { label: 'Lote 002', value: 'batch2' }
      ];

      this.serialNumberOptions = [
        { label: 'W0002P564', value: 'serial1' },
        { label: 'W0002P565', value: 'serial2' }
      ];
    } else if (this.filterMode === 'inventory-stock') {
      this.productOptions = [
        { label: 'Nitrato de amonio', value: 'nitrato' },
        { label: 'BESS', value: 'bess' }
      ];

      this.locationOptions = [
        { label: 'W0001/S/01/03/02', value: 'w0001' },
        { label: 'W0002/S/01/03/02', value: 'w0002' }
      ];
    } else if (this.filterMode === 'carbonFootprint') {
      this.shippingRefOptions = [
        { label: 'REF001', value: 'ref001' },
        { label: 'REF002', value: 'ref002' },
        { label: 'REF003', value: 'ref003' }
      ];

      this.mblCarbonOptions = [
        { label: 'MBL001', value: 'mbl001' },
        { label: 'MBL002', value: 'mbl002' },
        { label: 'MBL003', value: 'mbl003' }
      ];

      this.containerOptions = [
        { label: 'CONT001', value: 'cont001' },
        { label: 'CONT002', value: 'cont002' },
        { label: 'CONT003', value: 'cont003' }
      ];

      this.projectCarbonOptions = [];
    } else {
      this.companyOptions = [
        { label: 'CEC CHILE', value: 'company1' },
        { label: 'CEC PERÚ', value: 'company2' },
        { label: 'CEC COLOMBIA', value: 'company3' }
      ];

      this.userOptions = [
        { label: 'Alejandro Parra', value: 'user1' },
        { label: 'Boris A...', value: 'user2' }
      ];

      this.clientOptions = [
        { label: 'AGUNSA COLOMBIA SAS', value: 'client1' }
      ];
    }
  }

  /**
   * Handle date filter changes
   */
  onDateFilterChange(): void {
    this.filterService.updateDateFilter({
      type: this.dateType as any,
      number: parseInt(this.dateNumber),
      period: this.datePeriod as any
    });

    // For projects mode, reload filter options when dates change
    // This ensures filter dropdowns are updated with available options for the new date range
    if (this.filterMode === 'projects') {
      this.loadFilterOptionsWithCurrentFilters(() => {
        this.emitFilterChange();
      });
    } else {
      this.emitFilterChange();
    }
  }

  /**
   * Handle manual date picker changes (when user selects dates directly from calendar)
   */
  onDatePickerChange(): void {
    // For projects mode, reload filter options when dates change manually
    // This ensures filter dropdowns are updated with available options for the new date range
    if (this.filterMode === 'projects') {
      this.loadFilterOptionsWithCurrentFilters(() => {
        this.emitFilterChange();
      });
    } else {
      this.emitFilterChange();
    }
  }

  /**
   * Handle other filter changes
   */
  onOtherFilterChange(filterType: string, value: any, list?: any[]): void {
    let valueLabels = '';

    if (value && value.length > 0) {
      const filterLabels = (list || [])
        .filter(item => value.includes(item.value))
        .map(f => f.label);
      valueLabels = filterLabels.join(', ');
    }

    // Update active filter value in the service
    this.filterService.updateActiveFilterValue(filterType, valueLabels);

    // Update local component value first
    if (filterType === 'companies') {
      this.selectedCompanies = value;
    } else if (filterType === 'users') {
      this.selectedUsers = value;
    } else if (filterType === 'clients') {
      this.selectedClients = value;
    } else if (filterType === 'projects') {
      this.selectedProjects = value;
    } else if (filterType === 'merchandise') {
      this.selectedMerchandise = value;
    } else if (filterType === 'status') {
      this.selectedStatus = value;
    } else if (filterType === 'mbl') {
      this.selectedMBL = value;
    } else if (filterType === 'hbl') {
      this.selectedHBL = value;
    } else if (filterType === 'country') {
      this.selectedCountry = value;
    } else if (filterType === 'margin') {
      this.selectedMargin = value;
    } else if (filterType === 'filterProperty') {
      this.selectedFilterProperty = value;
    } else if (filterType === 'operationsProject') {
      this.selectedProject = value;
    } else if (filterType === 'operationsContainer') {
      this.selectedOperationsContainer = value;
    } else if (filterType === 'operationsServices') {
      this.selectedServices = value;
    } else if (filterType === 'operationsMBL') {
      this.selectedOperationsMBL = value;
    } else if (filterType === 'operationsHBL') {
      this.selectedOperationsHBL = value;
    } else if (filterType === 'bess') {
      this.selectedBESS = value;
    } else if (filterType === 'destinationTransportRef') {
      this.selectedDestinationTransportRef = value;
    }

    // *** NUEVO: Para selectores que no deben recargar sus propias opciones ***
    // Estos selectores muestran TODAS las opciones disponibles independientemente de la selección
    const selectorsWithStaticOptions = [
      'merchandise',   // Mercancía
      'status',        // Estado
      'hbl',          // HBL
      'margin'        // Margen en finance mode
    ];

    // Determine if the filter type should be static based on current mode
    const isStaticProject = filterType === 'projects' && (this.filterMode === 'projects' || this.filterMode === 'finance');
    const isStaticMBL = filterType === 'mbl' && this.filterMode === 'projects';
    const isStaticCountry = filterType === 'country' && this.filterMode === 'finance';

    // Si es un selector con opciones estáticas, solo actualizar el servicio y emitir cambio
    if (selectorsWithStaticOptions.includes(filterType) || isStaticProject || isStaticMBL || isStaticCountry) {
      // Actualizar FilterService
      this.filterService.updateFilter(filterType, value);
      // Emitir cambio para recargar los datos (NO las opciones)
      this.emitFilterChange();
      return;
    }

    // Para filterProperty en crm-totals mode, don't reload options (they don't change)
    // but update the service and emit filter change so crm-totals can reload
    if (this.filterMode === 'crm-totals' && filterType === 'filterProperty') {
      // Update FilterService now for filterProperty (null if cleared)
      this.filterService.updateFilter(filterType, value || null);
      // Clear amount values if filterProperty is cleared
      if (!value || value === null || value === undefined) {
        this.amountMin = null;
        this.amountMax = null;
        this.filterService.updateFilter('amountMin', null);
        this.filterService.updateFilter('amountMax', null);
      }
      // Emit filter change to trigger reload in crm-totals
      this.emitFilterChange();
      return;
    }

    // For companies in crm-totals mode, update service and emit filter change
    if (this.filterMode === 'crm-totals' && filterType === 'companies') {
      // Update FilterService
      this.filterService.updateFilter(filterType, value);
      // Emit filter change to trigger reload in crm-totals
      this.emitFilterChange();
      return;
    }

    // For Boolean filters (like withoutBudget), don't reload options
    // Just update the filter value and emit change
    if (filterType === 'withoutBudget') {
      this.emitFilterChange();
      return;
    }

    // Actualizar el servicio ANTES de recargar opciones
    // Esto asegura que loadFilterOptionsWithCurrentFilters use el valor correcto
    this.filterService.updateFilter(filterType, value);

    // Always reload filter options when any filter changes (including clearing)
    // The backend returns available options based on current filter selections
    // After options are reloaded, emit filter change to trigger data refresh
    this.loadFilterOptionsWithCurrentFilters(() => {
      // El servicio ya fue actualizado arriba, solo emitir el cambio
      this.emitFilterChange();
    });
  }

  /**
   * Handle Boolean filter changes (switch)
   */
  onBooleanFilterChange(filterType: string, value: boolean): void {
    // Update the filter service
    this.filterService.updateFilter(filterType, value);

    // Update active filter value in the service
    let valueLabel: string;
    if (filterType === 'inExecution') {
      valueLabel = value ? 'En Ejecución' : '';
    } else {
      valueLabel = value ? 'Sin Presupuesto' : 'Con Presupuesto';
    }
    this.filterService.updateActiveFilterValue(filterType, valueLabel);

    // Emit filter change immediately (no need to reload options for Boolean filters)
    this.emitFilterChange();
  }

  /**
   * Handle inExecution boolean filter change
   */
  onInExecutionChange(value: boolean): void {
    this.inExecution = value;
    this.onBooleanFilterChange('inExecution', value);
  }

  /**
   * Handle segmented change for withoutBudget filter
   */
  onWithoutBudgetSegmentedChange(value: number | string): void {
    // nz-segmented returns the index (0 or 1) when using nzOptions
    // Index 0 = 'Sin Presupuesto' (without) = true
    // Index 1 = 'Con Presupuesto' (with) = false
    const booleanValue = value === 0 || value === 'without';
    // Update local property
    this.withoutBudget = booleanValue;
    // Call the existing boolean filter change handler
    this.onBooleanFilterChange('withoutBudget', booleanValue);
  }

  /**
   * Load filter options with current filter values
   */
  private loadFilterOptionsWithCurrentFilters(onComplete?: () => void): void {
    // Trust parent authentication state instead of checking Odoo credentials
    if (!this.isAuthenticated) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoadingFilterOptions) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    const currentFilters = this.filterService.getCurrentFilters();

    // Prepare request body with current filters
    const requestBody = {
      dateFrom: currentFilters.dateFrom || this.startDate.toISOString(),
      dateTo: currentFilters.dateTo || this.endDate.toISOString(),
      otherFilters: this.buildOtherFiltersArray(currentFilters)
    };

    // Set loading states
    this.isLoadingFilterOptions = true;
    this.setLoadingStates(true);
    this.startRequest();

    this.reportsService.getReportFilters(this.reportKey, requestBody).pipe(
      take(1),
      finalize(() => {
        this.setLoadingStates(false);
        this.endRequest();
        this.isLoadingFilterOptions = false;
      })
    ).subscribe({
      next: (response) => {
        this.processFilterOptions(response);
        // Call onComplete callback if provided (to emit filter change after options are loaded)
        if (onComplete) {
          onComplete();
        }
      },
      error: (error) => {
        console.error('Error loading filter options with current filters:', error);
        // Call onComplete callback even on error to ensure filter change is emitted
        if (onComplete) {
          onComplete();
        }
      }
    });
  }

  /**
   * Build other filters array for the request using the centralized utility function
   */
  private buildOtherFiltersArray(currentFilters: any): any[] {
    return buildReportOtherFilters(currentFilters, this.filterMode);
  }

  /**
   * Emit filter change event
   */
  public emitFilterChange(): void {
    // Update FilterService FIRST with all current values, regardless of validation
    // This ensures components can access the latest filter values

    // Update dates in the service and emit dateChange$ to notify subscribers
    if (this.startDate && this.endDate) {
      this.filterService.updateDates(this.startDate, this.endDate);
    }

    // Update filters in the service before validation
    if (this.filterMode === 'crm-totals') {
      // Update companies filter
      if (this.selectedCompanies !== null && this.selectedCompanies !== undefined) {
        this.filterService.updateFilter('companies', this.selectedCompanies);
      } else {
        this.filterService.updateFilter('companies', null);
      }
      // Update filterProperty if set
      if (this.selectedFilterProperty !== null && this.selectedFilterProperty !== undefined) {
        this.filterService.updateFilter('filterProperty', this.selectedFilterProperty);
      } else {
        this.filterService.updateFilter('filterProperty', null);
      }

      // Don't update amount values here if they were just updated by debounce
      // The debounce already updated the service, so we don't want to overwrite with potentially stale local values
      // Just sync local values with service values to keep them in sync
      const currentServiceFilters = this.filterService.getCurrentFilters();

      // Read from service to get the latest values (updated by debounce)
      // This ensures we use the actual values in the service, not stale local values
      if (currentServiceFilters.amountMin !== undefined) {
        this.amountMin = currentServiceFilters.amountMin;
      }
      if (currentServiceFilters.amountMax !== undefined) {
        this.amountMax = currentServiceFilters.amountMax;
      }

      // Only update service if local values differ from service values
      // This prevents overwriting service with stale values
      if (currentServiceFilters.amountMin !== this.amountMin) {
        this.filterService.updateFilter('amountMin', this.amountMin);
      }
      if (currentServiceFilters.amountMax !== this.amountMax) {
        this.filterService.updateFilter('amountMax', this.amountMax);
      }

      // Emit a dummy filter update to ensure filterChange$ is triggered
      // This ensures crm-totals receives notification even if validation fails below
      this.filterService.updateFilter('_dateUpdate', Date.now());
    }

    // For crm-totals mode, always emit filterChange$ (already done via updateFilter calls above)
    // But only emit @Output event if there's a filter property and at least one amount value
    // This allows crm-totals to reload when dates or companies change, even without other filters
    if (this.filterMode === 'crm-totals') {
      const hasFilterProperty = this.selectedFilterProperty !== null && this.selectedFilterProperty !== undefined;
      // Allow 0 as a valid value, only exclude null/undefined
      const hasAmountValues = (this.amountMin !== null && this.amountMin !== undefined) ||
        (this.amountMax !== null && this.amountMax !== undefined);

      if (!hasFilterProperty || !hasAmountValues) {
        // Don't emit @Output event if conditions are not met
        // But filterChange$ was already emitted when updating filters above, so crm-totals will reload
        return;
      }
    }

    const currentFilters = this.filterService.getCurrentFilters();

    const filterEvent: FilterChangeEvent = {
      dateFrom: this.startDate.toISOString(),
      dateTo: this.endDate.toISOString(),
      companies: currentFilters.companies || this.selectedCompanies,
      users: currentFilters.users || this.selectedUsers,
      clients: currentFilters.clients || this.selectedClients,
      // Projects mode filters
      projects: currentFilters.projects || this.selectedProjects,
      merchandise: currentFilters.merchandise || this.selectedMerchandise,
      status: currentFilters.status || this.selectedStatus,
      mbl: currentFilters.mbl || this.selectedMBL,
      hbl: currentFilters.hbl || this.selectedHBL,
      // Inventory mode filters
      origin: currentFilters.origin || this.selectedOrigin,
      destination: currentFilters.destination || this.selectedDestination,
      supplier: currentFilters.supplier || this.selectedSupplier,
      product: currentFilters.product || this.selectedProduct,
      batch: currentFilters.batch || this.selectedBatch,
      serialNumber: currentFilters.serialNumber || this.selectedSerialNumber,
      location: currentFilters.location || this.selectedLocation,
      // Temperature mode filters
      bess: currentFilters.bess || this.selectedBESS,
      tracker: currentFilters.tracker || this.selectedTracker,
      lote: currentFilters.lote || this.selectedLote,
      temperatureMin: currentFilters.temperatureMin ?? this.temperatureMin,
      temperatureMax: currentFilters.temperatureMax ?? this.temperatureMax,
      // Carbon Footprint mode filters
      shippingRef: currentFilters.shippingRef || this.selectedShippingRef,
      mblCarbon: currentFilters.mblCarbon || this.selectedMBLCarbon,
      container: currentFilters.container || this.selectedContainer,
      projectCarbon: currentFilters.projectCarbon || this.selectedProjectCarbon,
      // Operations mode filters
      operationsProject: currentFilters.operationsProject || this.selectedProject,
      operationsContainer: currentFilters.operationsContainer || this.selectedOperationsContainer,
      operationsServices: currentFilters.operationsServices || this.selectedServices,
      operationsMBL: currentFilters.operationsMBL || this.selectedOperationsMBL,
      operationsHBL: currentFilters.operationsHBL || this.selectedOperationsHBL,
      dateFilterType: currentFilters.dateFilterType || this.selectedDateFilterType,  // 👈 AGREGAR ESTA LÍNEA
      // CRM Totals mode filters - use local component values to ensure they're up to date
      filterProperty: this.selectedFilterProperty ?? undefined,
      amountMin: this.amountMin ?? undefined,
      amountMax: this.amountMax ?? undefined,
      // Finance mode filters
      country: currentFilters.country || this.selectedCountry,
      margin: currentFilters.margin || this.selectedMargin,
      // CRM mode Boolean filters
      withoutBudget: currentFilters.withoutBudget !== undefined ? currentFilters.withoutBudget : this.withoutBudget,
      destinationTransportRef: currentFilters.destinationTransportRef || this.selectedDestinationTransportRef,
      // Projects mode Boolean filters
      inExecution: currentFilters.inExecution !== undefined ? currentFilters.inExecution : this.inExecution
    };

    this.filterChange.emit(filterEvent);
  }

  /**
   * Start tracking a request
   */
  private startRequest(): void {
    this.pendingRequests++;
  }

  /**
   * End tracking a request
   */
  private endRequest(): void {
    this.pendingRequests--;
    if (this.pendingRequests === 0) {
      this.filterReady.emit();
    }
  }

  // PDF helper methods
  getDateRange(): string {
    const start = this.startDate.toLocaleDateString('es-ES');
    const end = this.endDate.toLocaleDateString('es-ES');
    return `${start} - ${end}`;
  }

  getDateRangeDescription(): string {
    return `Últimos ${this.dateNumber} ${this.datePeriod}`;
  }

  getCompanyFilter(): string {
    if (this.selectedCompanies && this.selectedCompanies.length > 0) {
      const companyLabels = this.selectedCompanies.map(companyId => {
        const company = this.companyOptions.find(opt => opt.value === companyId);
        return company ? company.label : companyId;
      });
      return companyLabels.join(', ');
    }
    return 'Todas las compañías';
  }

  getUserFilter(): string {
    if (this.selectedUsers && this.selectedUsers.length > 0) {
      const userLabels = this.selectedUsers.map(userId => {
        const user = this.userOptions.find(opt => opt.value === userId);
        return user ? user.label : userId;
      });
      return userLabels.join(', ');
    }
    return 'Todos los usuarios';
  }

  getClientFilter(): string {
    if (this.selectedClients && this.selectedClients.length > 0) {
      const clientLabels = this.selectedClients.map(clientId => {
        const client = this.clientOptions.find(opt => opt.value === clientId);
        return client ? client.label : clientId;
      });
      return clientLabels.join(', ');
    }
    return 'Todos los clientes';
  }

  // Projects mode helper methods
  getProjectFilter(): string {
    if (this.selectedProjects && this.selectedProjects.length > 0) {
      const projectLabels = this.selectedProjects.map(projectId => {
        const project = this.projectOptions.find(opt => opt.value === projectId);
        return project ? project.label : projectId;
      });
      return projectLabels.join(', ');
    }
    return 'Todos los proyectos';
  }

  getMerchandiseFilter(): string {
    if (this.selectedMerchandise && this.selectedMerchandise.length > 0) {
      const merchandiseLabels = this.selectedMerchandise.map(merchandiseId => {
        const merchandise = this.merchandiseOptions.find(opt => opt.value === merchandiseId);
        return merchandise ? merchandise.label : merchandiseId;
      });
      return merchandiseLabels.join(', ');
    }
    return 'Toda la mercancía';
  }

  getStatusFilter(): string {
    if (this.selectedStatus && this.selectedStatus.length > 0) {
      const statusLabels = this.selectedStatus.map(statusId => {
        const status = this.statusOptions.find(opt => opt.value === statusId);
        return status ? status.label : statusId;
      });
      return statusLabels.join(', ');
    }
    return 'Todos los estados';
  }

  getMBLFilter(): string {
    if (this.selectedMBL && this.selectedMBL.length > 0) {
      const mblLabels = this.selectedMBL.map(mblId => {
        const mbl = this.mblOptions.find(opt => opt.value === mblId);
        return mbl ? mbl.label : mblId;
      });
      return mblLabels.join(', ');
    }
    return 'Todos los MBL';
  }

  getHBLFilter(): string {
    if (this.selectedHBL && this.selectedHBL.length > 0) {
      const hblLabels = this.selectedHBL.map(hblId => {
        const hbl = this.hblOptions.find(opt => opt.value === hblId);
        return hbl ? hbl.label : hblId;
      });
      return hblLabels.join(', ');
    }
    return 'Todos los HBL';
  }

  // Filter option function for searchable selects
  filterOption = (input: string, option: any): boolean => {
    if (!input || !option) {
      return true;
    }
    const searchValue = input.toLowerCase();
    const optionLabel = option?.nzLabel?.toLowerCase() || '';
    return optionLabel.includes(searchValue);
  }

  // Temperature mode helper methods
  onTemperatureChange(): void {
    this.filterService.updateFilter('temperatureMin', this.temperatureMin);
    this.filterService.updateFilter('temperatureMax', this.temperatureMax);
    this.emitFilterChange();
  }

  onTemperatureSliderChange(values: number[]): void {
    if (values && values.length === 2) {
      this.temperatureMin = values[0];
      this.temperatureMax = values[1];
      this.onTemperatureChange();
    }
  }

  getTemperatureFilter(): string {
    return `${this.temperatureMin}° - ${this.temperatureMax}°`;
  }

  // CRM Totals mode helper methods
  /**
   * Handle filterProperty change - allows clearing selection
   */
  onFilterPropertyChange(value: string | null): void {
    this.selectedFilterProperty = value || null;

    // Clear amount values if filterProperty is cleared
    if (!value || value === null || value === undefined) {
      this.amountMin = null;
      this.amountMax = null;
      this.filterService.updateFilter('amountMin', null);
      this.filterService.updateFilter('amountMax', null);
    }

    // Update FilterService
    this.filterService.updateFilter('filterProperty', value || null);

    // Emit filter change to trigger reload in crm-totals
    this.emitFilterChange();
  }

  onAmountChange(field?: 'min' | 'max', value?: any): void {
    // If value is provided (from ngModelChange event), use it directly
    // Otherwise, read from model (for backward compatibility)
    if (field === 'max' && value !== undefined) {
      // Capture the new value directly from ngModelChange event for amountMax
      // This ensures we get the actual value that was just typed
      const numValue = (value === null || value === undefined || value === '') ? null : Number(value);
      this.amountMax = numValue;
      this.latestAmountMax = numValue;
    } else if (field === 'min' && value !== undefined) {
      // Capture the new value directly from ngModelChange event for amountMin
      const numValue = (value === null || value === undefined || value === '') ? null : Number(value);
      this.amountMin = numValue;
      this.latestAmountMin = numValue;
    } else {
      // Fallback: update from model (may have timing issues)
      this.latestAmountMin = this.amountMin;
      this.latestAmountMax = this.amountMax;
    }

    // Trigger debounce with latest captured values
    this.amountChangeSubject$.next({
      amountMin: this.latestAmountMin ?? this.amountMin,
      amountMax: this.latestAmountMax ?? this.amountMax
    });
  }

  onAmountSliderChange(values: number | number[]): void {
    if (Array.isArray(values) && values.length === 2) {
      this.amountMin = values[0];
      this.amountMax = values[1];
      // Update filter service immediately
      this.filterService.updateFilter('amountMin', values[0]);
      this.filterService.updateFilter('amountMax', values[1]);
      // Emit immediately for slider (no debounce needed)
      // emitFilterChange will validate conditions for crm-totals mode
      this.emitFilterChange();
    }
  }

  getAmountFilter(): string {
    const minStr = this.amountMin !== null ? this.formatAmount(this.amountMin) : '';
    const maxStr = this.amountMax !== null ? this.formatAmount(this.amountMax) : '';
    if (minStr && maxStr) {
      return `${minStr} - ${maxStr}`;
    } else if (minStr) {
      return `Desde ${minStr}`;
    } else if (maxStr) {
      return `Hasta ${maxStr}`;
    }
    return '';
  }

  formatAmount(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  formatAmountTooltip = (value: number | null): string => {
    return this.formatAmount(value);
  }

}
